import {
  ClimateRiskType,
  PropertyErosionRisk,
  PropertyErosionScores,
  PropertyFloodRisk,
  PropertyNoisePollutionRisk,
} from "../types/appraiser.types";
import colors from "common/styles/colors";

export const determineBackgroundColor = (risk: ClimateRiskType) => {
  switch (risk) {
    case ClimateRiskType.None:
      return colors.green;
    case ClimateRiskType.Low:
    case ClimateRiskType.Medium:
      return colors.orange;
    case ClimateRiskType.High:
      return colors.red;
  }
};

export const isIn10YearFloodZone = (floodRisk: ClimateRiskType) =>
  floodRisk === ClimateRiskType.High;

export const isIn100YearFloodZone = (floodRisk: ClimateRiskType) =>
  floodRisk === ClimateRiskType.High || floodRisk === ClimateRiskType.Medium;

export const isIn1000YearFloodZone = (floodRisk: ClimateRiskType) =>
  floodRisk === ClimateRiskType.High ||
  floodRisk === ClimateRiskType.Medium ||
  floodRisk === ClimateRiskType.Low;

export const floodRiskFromZoneAnswers = (
  in10Year: boolean,
  in100Year: boolean,
  in1000Year: boolean
): ClimateRiskType => {
  if (in10Year) return ClimateRiskType.High;
  if (in100Year) return ClimateRiskType.Medium;
  if (in1000Year) return ClimateRiskType.Low;
  return ClimateRiskType.None;
};

export const isClimateRiskType = (value: unknown): value is ClimateRiskType =>
  typeof value === "string" &&
  Object.values(ClimateRiskType).includes(value as ClimateRiskType);

export const getFloodRiskFormValue = (
  floodRisk?: ClimateRiskType | PropertyFloodRisk | string | null
): ClimateRiskType | null => {
  const value =
    floodRisk && typeof floodRisk === "object"
      ? floodRisk.floodRisk
      : floodRisk;

  return isClimateRiskType(value) ? value : null;
};

// The erosion value is a relative index, not a percentage or an area share.
export const formatErosionIndex = (value: number) =>
  value.toFixed(2).replace(".", ",");

export const ErosionScenarios: {
  key: keyof PropertyErosionScores;
  label: string;
  gloss: string;
}[] = [
  {
    key: "rcp26",
    label: "RCP 2.6",
    gloss: "optimistinis – emisijos ryškiai mažėja",
  },
  {
    key: "rcp45",
    label: "RCP 4.5",
    gloss: "vidutinis – artimas dabartinei politikos trajektorijai",
  },
  {
    key: "rcp85",
    label: "RCP 8.5",
    gloss: "pesimistinis – emisijos toliau auga",
  },
];

export const getErosionAreaName = (erosionRisk: PropertyErosionRisk) =>
  [erosionRisk.cadastralArea, erosionRisk.municipality]
    .filter(Boolean)
    .join(", ");

// Scenario values rarely match exactly, so a spread this small still counts as flat.
const EROSION_SPREAD_ABSOLUTE_TOLERANCE = 0.5;
const EROSION_SPREAD_RELATIVE_TOLERANCE = 0.1;

export const getErosionScenarioSpreadDescription = (
  erosionScores: PropertyErosionScores,
  riskLevel?: ClimateRiskType
): string | undefined => {
  const values = ErosionScenarios.map(({ key }) => erosionScores[key]);
  const highest = Math.max(...values);
  const spread = highest - Math.min(...values);

  const isFlat =
    spread <= EROSION_SPREAD_ABSOLUTE_TOLERANCE ||
    (highest > 0 && spread / highest <= EROSION_SPREAD_RELATIVE_TOLERANCE);

  if (isFlat) {
    if (riskLevel === ClimateRiskType.High) return undefined;
    return "Rodiklis panašus pagal visus scenarijus – rizika nepriklauso nuo emisijų kelio. Taip būna žemyninėse vietovėse, kur vyrauja upių šlaitų erozija.";
  }

  if (erosionScores.rcp85 >= erosionScores.rcp26) {
    return "Rodiklis ryškiai auga pagal scenarijus – didžioji dalis rizikos išryškėja tik esant didelėms emisijoms.";
  }

  return "Rodiklis reikšmingai skiriasi pagal scenarijus.";
};

export const getHeatRiskDescription = (hotDaysCount: number) => {
  if (hotDaysCount <= 5) return "Ne daugiau nei 5 karštos dienos.";
  else if (hotDaysCount <= 12) return "6-12 karštų dienų.";
  return "Daugiau nei 12 karštų dienų.";
};

export const getWindRiskDescription = (windyDaysCount: number) => {
  if (windyDaysCount <= 15) return "Ne daugiau nei 15 vėjuotų dienų.";
  else if (windyDaysCount <= 40) return "16-40 vėjuotų dienų.";
  return "Daugiau nei 40 vėjuotų dienų.";
};

export const getAirPollutionRiskDescription = (pm_10: number) => {
  if (pm_10 < 15) return "KD koncentracija mažesnė nei 15 μg/m3.";
  if (pm_10 < 20) return "KD koncentracija yra nuo 15 iki 20 μg/m3.";
  return "KD koncentracija yra 20 μg/m3 ar didesnė.";
};

type NoiseLevelRange = { minLevel: number; maxLevel?: number | null };

const formatNoiseLevelRange = (
  minLevel: number,
  maxLevel?: number | null
) => {
  if (maxLevel == null) {
    return `virš ${minLevel} dB`;
  }
  if (minLevel === maxLevel) {
    return `${minLevel} dB`;
  }
  return `${minLevel}–${maxLevel} dB`;
};

const mergeAdjacentNoiseLevels = (levels: NoiseLevelRange[]) => {
  const sorted = [...levels].sort((a, b) => a.minLevel - b.minLevel);
  const merged: NoiseLevelRange[] = [];

  for (const level of sorted) {
    const last = merged[merged.length - 1];
    const isAdjacent =
      last &&
      (last.maxLevel == null || last.maxLevel >= level.minLevel - 1);

    if (!isAdjacent) {
      merged.push({ minLevel: level.minLevel, maxLevel: level.maxLevel });
      continue;
    }

    if (last.maxLevel == null || level.maxLevel == null) {
      last.maxLevel = null;
    } else if (level.maxLevel > last.maxLevel) {
      last.maxLevel = level.maxLevel;
    }
  }

  return merged;
};

const joinLithuanianList = (items: string[]) => {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} ir ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} ir ${items[items.length - 1]}`;
};

const getNoiseQueryMethodDescription = (
  queryMethod?: PropertyNoisePollutionRisk["queryMethod"]
) => {
  if (queryMethod === "plot") {
    return "Intervalai gauti pagal sklypo ribas: įtrauktos visos triukšmo zonos, kurios kerta sklypą.";
  }
  if (queryMethod === "radius") {
    return "Sklypo ribų nėra, todėl vertinta 15 m spinduliu aplink turtą: įtrauktos visos zonos, kurios kerta šį spindulį.";
  }
  return "Intervalai gauti iš zonų, kertančių 30×30 m kvadratą aplink turtą.";
};

export const getNoisePollutionRiskDescription = (
  minLevel?: number,
  maxLevel?: number | null,
  levels?: NoiseLevelRange[],
  queryMethod?: PropertyNoisePollutionRisk["queryMethod"]
) => {
  const uniqueLevels = levels?.length
    ? levels
    : minLevel != null
      ? [{ minLevel, maxLevel }]
      : [];

  if (!uniqueLevels.length) {
    return "Triukšmo sklaida nenustatyta.";
  }

  const ranges = mergeAdjacentNoiseLevels(uniqueLevels).map((level) =>
    formatNoiseLevelRange(level.minLevel, level.maxLevel)
  );
  return `Triukšmo lygis siekia ${joinLithuanianList(ranges)}. ${getNoiseQueryMethodDescription(
    queryMethod
  )}`;
};